/**
 * Форма квеста: метаданные плюс состав и порядок раундов.
 *
 * Порядок раундов задаётся порядком выбранных фотографий: backend нумерует
 * их так, как они пришли в photoIds.
 */
import { useState } from "react";
import { Create, Edit, useForm } from "@refinedev/antd";
import { useSelect } from "@refinedev/core";
import { Alert, Col, DatePicker, Form, Input, Row, Select, Space, Typography } from "antd";
import dayjs from "dayjs";

import { CONTENT_STATUSES, QUEST_TYPES, SELECTABLE_QUEST_TYPES, formatYear } from "../../lib/constants";

interface Photo {
  id: string;
  description?: string;
  year?: number;
  status?: string;
}

interface Tag {
  slug: string;
  nameEn?: string;
  nameRu?: string;
}

interface QuestFormValues {
  title?: string;
  description?: string;
  type?: string;
  status?: string;
  tags?: string[];
  activeDate?: string;
  photoIds?: string[];
  photos?: Photo[];
  roundCount?: number;
}

/**
 * @param worldRounds — задан у квеста «Весь мир»: состав его раундов ведёт
 *   база, и выбирать снимки руками нечего — показываем только их число.
 */
function Fields({ initialPhotos, worldRounds }: { initialPhotos?: Photo[]; worldRounds?: number }) {
  const form = Form.useFormInstance<QuestFormValues>();
  const [questType, setQuestType] = useState<string | undefined>(form.getFieldValue("type"));

  // Список фотографий для выбора раундов. Поиск идёт на сервере, поэтому
  // выбирать можно и из большой библиотеки, а не только из первой страницы.
  const { options, onSearch } = useSelect<Photo>({
    resource: "photos",
    optionLabel: (photo) =>
      `${photo.description || "Без описания"} · ${formatYear(photo.year)}`,
    optionValue: "id",
    pagination: { pageSize: 50 },
    onSearch: (value) => [{ field: "search", operator: "contains", value }],
    // Уже выбранные фото подгружаются отдельно, чтобы в поле были подписи, а не UUID.
    defaultValue: initialPhotos?.map((photo) => photo.id),
  });

  // Теги выбираются из справочника: свободный ввод backend отвергает, потому
  // что код вне справочника не покажется игроку ни на одном языке.
  const { options: tagOptions } = useSelect<Tag>({
    resource: "tags",
    optionLabel: (tag) => `${tag.nameRu ?? tag.slug} · ${tag.nameEn ?? ""}`,
    optionValue: "slug",
    pagination: { pageSize: 200 },
  });

  return (
    <Row gutter={24}>
      <Col xs={24} lg={14}>
        <Form.Item
          label="Название"
          name="title"
          rules={[{ required: true, message: "Укажите название" }]}
        >
          <Input placeholder="Городские силуэты XX века" />
        </Form.Item>

        <Form.Item label="Описание" name="description">
          <Input.TextArea rows={3} placeholder="Чем интересна эта подборка" />
        </Form.Item>

        {worldRounds !== undefined ? (
          <Alert
            type="info"
            showIcon
            message={`Раундов: ${worldRounds.toLocaleString("ru-RU")} — все опубликованные снимки`}
            description="Состав квеста «Весь мир» собирается автоматически: публикация снимка добавляет его сюда, снятие с публикации или удаление — убирает. Править можно название, описание, теги, обложку и статус."
            style={{ marginBottom: 24 }}
          />
        ) : (
          <Form.Item
            label="Раунды"
            name="photoIds"
            tooltip="Порядок выбора определяет порядок раундов в игре"
            rules={[{ required: true, message: "Добавьте хотя бы одну фотографию" }]}
          >
            <Select
              mode="multiple"
              placeholder="Выберите фотографии"
              style={{ width: "100%" }}
              options={options}
              showSearch
              // Фильтрация идёт на сервере, локальную отключаем: иначе она
              // отбросит подгруженные по запросу варианты.
              filterOption={false}
              onSearch={onSearch}
            />
          </Form.Item>
        )}

        {worldRounds === undefined && (
          <Alert
            type="info"
            showIcon
            message="Публикуются только опубликованные фотографии"
            description="Если квест публикуется, убедитесь, что все его снимки тоже в статусе «Опубликовано» — иначе игроки увидят пустой раунд."
            style={{ marginBottom: 24 }}
          />
        )}
      </Col>

      <Col xs={24} lg={10}>
        <Form.Item label="Тип" name="type" initialValue="QUEST_TYPE_THEMED">
          <Select
            disabled={worldRounds !== undefined}
            options={(worldRounds !== undefined ? QUEST_TYPES : SELECTABLE_QUEST_TYPES).map((item) => ({
              value: item.value,
              label: item.label,
            }))}
            onChange={(value) => setQuestType(value as string)}
          />
        </Form.Item>

        <Form.Item
          label="Дата квеста дня"
          name="activeDate"
          tooltip="Обязательна для типа «Квест дня». На каждую дату может быть только один такой квест."
          getValueProps={(value?: string) => ({ value: value ? dayjs(value) : undefined })}
          normalize={(value: dayjs.Dayjs | null) => value?.format("YYYY-MM-DD") ?? ""}
          rules={
            questType === "QUEST_TYPE_DAILY"
              ? [{ required: true, message: "Для квеста дня нужна дата" }]
              : []
          }
        >
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
        </Form.Item>

        <Form.Item label="Статус" name="status" initialValue="CONTENT_STATUS_DRAFT">
          <Select
            options={CONTENT_STATUSES.map((item) => ({ value: item.value, label: item.label }))}
          />
        </Form.Item>

        <Form.Item label="Теги" name="tags">
          <Select
            mode="multiple"
            placeholder="Выберите теги из справочника"
            options={tagOptions}
            optionFilterProp="label"
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Space direction="vertical">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Максимум очков за квест: {(form.getFieldValue("photoIds")?.length ?? 0) * 1000}
          </Typography.Text>
        </Space>
      </Col>
    </Row>
  );
}

export function QuestCreate() {
  const { formProps, saveButtonProps } = useForm<QuestFormValues>({ resource: "quests" });

  return (
    <Create title="Новый квест" saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Fields />
      </Form>
    </Create>
  );
}

export function QuestEdit() {
  const { formProps, saveButtonProps, query } = useForm<QuestFormValues>({
    resource: "quests",
    // В ответе приходят полные объекты фотографий, а форма работает
    // со списком идентификаторов.
    queryOptions: {
      select: (response) => ({
        ...response,
        data: {
          ...response.data,
          photoIds: (response.data.photos ?? []).map((photo: Photo) => photo.id),
        },
      }),
    },
  });

  return (
    <Edit title="Редактирование квеста" saveButtonProps={saveButtonProps}>
      <Form {...formProps} layout="vertical">
        <Fields
          initialPhotos={query?.data?.data?.photos}
          worldRounds={
            query?.data?.data?.type === "QUEST_TYPE_WORLD" ? (query.data.data.roundCount ?? 0) : undefined
          }
        />
      </Form>
    </Edit>
  );
}
